import { AnyRoute, Navigate, Outlet, createRoute } from '@tanstack/react-router'
import { CircularProgress } from 'ui/components'
import { z } from 'zod'

import { AuthLayout } from './AuthLayout'
import * as SC from './components/style'
import { AUTH_PATH } from './constants'
import { ApplicationValidateError, ResetPassword, SetPassword, SignIn } from './routes'

const forgotPasswordSearchSchema = z.object({
  returnUrl: z.string().optional(),
  expired: z.boolean().optional()
})

const passwordResetSearchSchema = z.object({
  returnUrl: z.string().optional(),
  key: z.string().optional()
})

export const routeAuthTree = <T extends AnyRoute>(rootRoute: T) => {
  const authRoute = createRoute({
    getParentRoute: () => rootRoute,
    validateSearch: (search: Record<'returnUrl', string>): Record<'returnUrl', unknown> | object => {
      if (search.returnUrl) {
        return {
          returnUrl: search.returnUrl
        }
      } else return {}
    },
    path: AUTH_PATH,
    notFoundComponent: () => <Navigate to={window.location.pathname + window.location.search} />,
    pendingComponent: () => (
      <SC.SpinnerContainer>
        <CircularProgress centered />
      </SC.SpinnerContainer>
    ),
    component: () => (
      <AuthLayout>
        <Outlet />
      </AuthLayout>
    )
  })

  const applicationValidationErrorRoute = createRoute({
    getParentRoute: () => authRoute,
    path: 'application-validate-error',
    validateSearch: (search: Record<'error', undefined>): Record<'errorMessage', undefined> => ({
      errorMessage: search.error || undefined
    }),
    component: ApplicationValidateError
  })

  const loginRoute = createRoute({
    getParentRoute: () => authRoute,
    beforeLoad: ({ context }) => {
      if (context.auth.hasSession) {
        // useSmartRedirect hook perform redirects to the auth page
      }
    },
    path: 'login',
    component: () => <SignIn />
  })

  const forgotPasswordRoute = createRoute({
    getParentRoute: () => authRoute,
    validateSearch: (search: Record<string, string>) => forgotPasswordSearchSchema.parse(search),
    path: 'forgot-password',
    component: () => <ResetPassword />
  })

  const passwordResetRoute = createRoute({
    getParentRoute: () => authRoute,
    validateSearch: (search: Record<string, string>) => passwordResetSearchSchema.parse(search),
    path: 'password-reset',
    component: () => <SetPassword />
  })

  return authRoute.addChildren([applicationValidationErrorRoute, loginRoute, forgotPasswordRoute, passwordResetRoute])
}
